import EventBus from '@/event-bus';
import Shop from '@/shop';
import _Singleton from '@/tools/singleton';
import _isNaN from 'lodash/isNaN';

import options from '@/ui/ui_accessories/default-selectors.json';
import optionsProduct from '@/ui/ui_product/default-selectors.json';
import defaultProductSettings from '@/ui/ui_product/default-settings.json';
import defaultSettings from '@/ui/ui_accessories/default-settings.json';

import getProductAccessoriesData from '@/helpers/getProductAccessoriesData';
import haveSameKeys from '@/helpers/haveSameKeys';

/**
 * @class
 * @private
 *
 * @description
 * DOM + Accessories
 */
const AccessoriesDOM = function() {
  this._init();
};

/**
 * @method
 * @private
 * @description
 * Инициализация происходит в ui_product после того как будет получен productJSON
 * @return {void}
 */
AccessoriesDOM.prototype._init = function() {
  const self = this;
  self.options = options;
  self.defaultSettings = defaultSettings;
  self.locale = Shop.config.config.locale;

  return self;
};

/**
 * @method
 * @private
 * @description
 * Рендер опций
 * @param {Object} action - данные о товаре
 * @return {void}
 */
AccessoriesDOM.prototype._renderAccessories = function(productNode, action) {
  const self = this;

  if (!action.productJSON || !action.productJSON.accessories || !productNode) { return; }

  // Вешаем класс, если в JSON товара есть аксессуары
  if (Object.keys(action.productJSON.accessories).length) {
    productNode.classList.add(defaultProductSettings.classes.withAccessories);
  }

  const accessoriesNode = productNode.querySelector(self.options.accessories.attr);

  // Если аксессуары уже отрендерены, то ничего не делаем
  if (accessoriesNode && accessoriesNode.classList.contains(self.defaultSettings.classes.inited)) { return; }

  const accessoriesTemplate = productNode.querySelector(self.options.accessoriesTemplate.attr);
  const accessoryValuesTemplate = productNode.querySelector(self.options.accessoryValuesTemplate.attr);

  if (!accessoriesNode || !accessoriesTemplate || !accessoryValuesTemplate) { return; }

  const accessoryItemNode = accessoriesTemplate.content.querySelector(self.options.accessoriesItem.attr);

  if (!accessoryItemNode) { return; }

  Object.values(action.productJSON.accessories).forEach(accessoryData => {
    const itemCloneNode = accessoryItemNode.cloneNode(true);
    const valuesNode = itemCloneNode.querySelector(self.options.accessoryValues.attr);
    const nameNode = itemCloneNode.querySelector(self.options.accessoryName.attr);
    const minMaxCountNode = itemCloneNode.querySelector(self.options.accessoryMinMaxCount.attr);

    if (nameNode) {
      nameNode.textContent = accessoryData.name;
    }

    self.setMinMaxCount(accessoryData, minMaxCountNode);
    self._renderAccessoryValues(accessoryData, valuesNode, accessoryValuesTemplate);

    itemCloneNode.dataset[self.options.accessoriesItemId.data] = accessoryData.id;

    accessoriesNode.appendChild(itemCloneNode);

    self._initAccessoryValues(accessoryData, valuesNode, productNode);
  });

  accessoriesNode.classList.add(self.defaultSettings.classes.inited);

  // Вешаем класс, что аксессуары инициализированы
  productNode.classList.add(defaultProductSettings.classes.accessoriesInited);

  EventBus.publish('accessories-rendered:insales:ui_accessories', {
    productNode,
    accessoriesNode,
    productJSON: action.productJSON
  });

  EventBus.subscribe('accessories-errors:insales:cart', function(cartData) {
    self.highlightErrors(cartData, action.productJSON, productNode);
  });
};

/**
 * @private
 * @description
 * Рендер значений аксессуаров
 * @param {Object} accessory - данные об аксессуаре
 * @param {Object} valuesNode - нода для рендера значений аксессуаров
 * @param {Object} template - шаблон для рендера значений аксессуаров
 * @return {void}
 */
AccessoriesDOM.prototype._renderAccessoryValues = function(accessory, valuesNode, template) {
  const self = this;

  let checkedCount = 0;

  accessory.accessory_values.forEach((valueData, index) => {
    let adjustedIndex = index + 1;

    const valueNode = template.content.querySelector(self.options.accessoryValuesItem.attr).cloneNode(true);
    const itemNameNode = valueNode.querySelector(self.options.accessoryValuesItemName.attr);
    const itemPriceNode = valueNode.querySelector(self.options.accessoryValuesitemPrice.attr);

    if (itemNameNode) {
      itemNameNode.textContent = valueData.name;
    }

    if (itemPriceNode) {
      itemPriceNode.textContent = Shop.money.format(valueData.price);
      itemPriceNode.dataset[self.options.accessoryValuesitemPrice.data] = valueData.price;
    }

    if (valueNode) {
      valueNode.dataset[self.options.accessoryValuesItemId.data] = valueData.id;

      const valueInput = document.createElement('input');
      valueInput.name = `accessory_value_ids[${accessory.id}]`;
      valueInput.value = valueData.id;

      if (accessory.min_count === 1 && accessory.max_count === 1) {
        valueInput.type = 'radio';
        valueInput.checked = adjustedIndex === 1;
      } else {
        valueInput.type = 'checkbox';
        // Отмечаем чекбоксы пока не достигнем минимального количества
        valueInput.checked = adjustedIndex <= accessory.min_count;
        // Если min_count равно количеству значений, то тогда отключаем все чекбоксы
        valueInput.disabled = accessory.accessory_values.length === accessory.min_count;

        checkedCount += valueInput.checked ? 1 : 0;

        // Если для опции задано максимально количество и количество выбранных опций равно максимальному количеству, то отключаем все остальные чекбоксы
        if (accessory.max_count && adjustedIndex > accessory.max_count && checkedCount === accessory.max_count) {
          valueInput.disabled = true;
        }
      }

      valueNode.prepend(valueInput);
    }

    valuesNode.appendChild(valueNode);
  });
};

/**
 * @private
 * @description
 * Инициализация обработчиков событий для аксессуаров
 * @param {Object} accessoryData - данные об аксессуаре
 * @param {Object} accessoryValuesNode - нода для рендера значений аксессуаров
 * @param {Object} productNode - нода продукта
 * @return {void}
 */
AccessoriesDOM.prototype._initAccessoryValues = function(accessoryData, accessoryValuesNode, productNode) {
  const self = this;

  const accessoryItemValueNodes = Array.from(accessoryValuesNode.querySelectorAll(self.options.accessoryValuesItem.attr));

  accessoryItemValueNodes.forEach(accessoryItemValueNode => {
    accessoryItemValueNode.addEventListener('change', function(e) {
      // Убираем класс с ошибкой
      accessoryItemValueNode
        .closest(self.options.accessoriesItem.attr)
        .classList.remove(defaultSettings.classes.hasError);

      const checkedValuesItemNodes = Array.from(accessoryValuesNode.querySelectorAll('input:checked'));
      const checkedCheckboxNodes = checkedValuesItemNodes.filter(item => item.type === 'checkbox');

      if (checkedCheckboxNodes.length >= parseInt(accessoryData.max_count)) {
        Array.from(accessoryValuesNode.querySelectorAll('input[type=checkbox]:not(:checked)'))
          .forEach(checkbox => { checkbox.disabled = true; });
      } else {
        Array.from(accessoryValuesNode.querySelectorAll('input[type=checkbox]'))
          .forEach(checkbox => { checkbox.disabled = false; });
      }

      const eventData = {
        productNode,
        accessoryData,
        accessoryValuesNode,
        accessoryValuesItemNode: accessoryItemValueNode,
        checkedValuesItemNodes
      };

      EventBus.publish('accessory_value_changed:insales:ui_accessories', eventData);
    });
  });
};

/**
 * @method
 * @private
 * @description
 * Получение активных опций
 * @param {Object} productNode - нода продукта
 * @return {Array}
 */
AccessoriesDOM.prototype._activeAccessories = function(productNode) {
  const self = this;

  const accessoriesNode = productNode.querySelector(self.options.accessories.attr);
  if (!accessoriesNode) { return; }
  const selectedAccessories = accessoriesNode.querySelectorAll(self.options.accessoriesItem.attr);
  const activeAccessories = [];

  selectedAccessories.forEach(accessory => {
    const title = accessory.querySelector(self.options.accessoryName.attr).textContent;
    const selectedValues = Array.from(
      accessory.querySelectorAll(`input:checked + ${self.options.accessoryValuesItemName.attr}`)
    ).map(element => element.textContent.trim());

    if (selectedValues.length > 0) {
      activeAccessories.push({ [title]: selectedValues });
    }
  });

  return activeAccessories;
};

/**
 * @private
 * @description
 * Установка минимального и максимального количества аксессуаров
 * @param {Object} accessoryData - данные об аксессуаре
 * @param {Object} minMaxCountNode - нода для рендера минимального и максимального количества аксессуаров
 * @return {void}
 */
AccessoriesDOM.prototype.setMinMaxCount = function(accessoryData, minMaxCountNode) {
  if (!minMaxCountNode) { return; }
  if (
    // Если не заданы min_count и max_count
    !accessoryData.min_count && !accessoryData.max_count
    // Если min_count и max_count равны 1
    || accessoryData.min_count === 1 && accessoryData.max_count === 1
    // Если min_count и max_count равны количеству значений
    || (
      accessoryData.min_count === accessoryData.max_count
      && accessoryData.accessory_values.length === accessoryData.min_count
      && accessoryData.max_count === accessoryData.accessory_values.length
      )
  ) { return; }

  const self = this;

  const optionsData = (
    minMaxCountNode.dataset[self.options.accessoryMinMaxCount.data]
    ? JSON.parse(minMaxCountNode.dataset[self.options.accessoryMinMaxCount.data]) : {}
  );
  let minCountText = '';
  let maxCountText = '';

  if (accessoryData.min_count) {
    const message = optionsData.minCountMessage || self._getMessageByLocale('minCountMessage');

    minCountText = `${message} ${accessoryData.min_count}`;
  }

  if (accessoryData.max_count && accessoryData.max_count <= accessoryData.accessory_values.length) {
    const message = optionsData.maxCountMessage || self._getMessageByLocale('maxCountMessage');

    maxCountText = `${message} ${accessoryData.max_count}`;
  }

  if (minCountText && !maxCountText) {
    minMaxCountNode.innerHTML = `(${minCountText})`;
  } else if (!minCountText && maxCountText) {
    minMaxCountNode.innerHTML = `(${maxCountText})`;
  } else if (minCountText && maxCountText) {
    minMaxCountNode.innerHTML = `(${minCountText} ${maxCountText})`;
  }
};

/**
 * @private
 *
 * @description
 * Если есть аксессуары (опции), то учитываем их в цене товара
 *
 * @param {Object} productNode - нода товара
 * @param {Object} productData - данные о товаре
 * @param {Boolean} isForCartUpdate - вызов прилетел после изменения количества товара в корзине?
 * @return {void}
 */
AccessoriesDOM.prototype.priceWithAccessories = function(productNode, productData, isForCartUpdate = false) {
  if (!productNode || !productData) { return; }

  const self = this;

  const accessoriesNode = productNode.querySelector(self.options.accessories.attr);

  if (!accessoriesNode) { return; }

  const accessoryItems = Array.from(productNode.querySelectorAll(`${self.options.accessoryValuesItem.attr} input`));

  if (!accessoryItems.length) { return; }

  self.calculatePricesWithAccessories(productNode, productData, isForCartUpdate);

  // Если метод был вызван после обновления корзины (изменения количества товар), то слушатели не трогаем
  if (isForCartUpdate) {
    return;
  }

  // Удаляем старые обработчики событий при смене варианта
  Array.from(accessoryItems).forEach(accessory => {
    accessory.removeEventListener('change', self.changeHandler);
  });

  // Обработчик события
  self.changeHandler = function() {
    self.calculatePricesWithAccessories(productNode, productData);
  };

  // Добавляем новые обработчики событий при смене варианта
  Array.from(accessoryItems).forEach(accessory => {
    accessory.addEventListener('change', self.changeHandler);
  });
};

/**
 * @private
 *
 * @description
 * Перерасчёт цен с учётом цен аксессуаров (опций)
 *
 * @param {Object} productNode - нода товара
 * @param {Object} productData - данные о товаре
 * @param {Boolean} isForCartUpdate - вызов прилетел после изменения количества товара в корзине?
 *
 * @return {void}
 */
AccessoriesDOM.prototype.calculatePricesWithAccessories = function(productNode, productData, isForCartUpdate = false) {
  const self = this;

  const accessoriesData = getProductAccessoriesData(productNode);

  const price = parseFloat((productData.action && productData.action.price) ? productData.action.price : productData.price);
  const oldPrice = parseFloat((productData.action && productData.action.old_price) ? productData.action.old_price : productData.old_price);
  const priceFromCart = parseFloat(self.getPriceFromCart(productData, accessoriesData));

  const oldPriceNode = productNode.querySelector(optionsProduct.oldPrice.attr);
  const saleValueNode = productNode.querySelector(optionsProduct.saleValue.attr);

  const chosenAccessoriesNodes = Array.from(productNode.querySelectorAll(`${self.options.accessoryValuesItem.attr} input:checked`));

  // Если выбранных опций нет, то просто обновляем цену товара
  if (!chosenAccessoriesNodes.length) {
    self.updatePrice(productNode, optionsProduct.priceFromCart.attr, priceFromCart || price);
    self.updatePrice(productNode, optionsProduct.price.attr, price);

    if (oldPriceNode && oldPrice && (price || priceFromCart) && (oldPrice > price || oldPrice > priceFromCart)) {
      productNode.classList.remove(defaultProductSettings.classes.withoutOldPrice);
      productNode.classList.add(defaultProductSettings.classes.withOldPrice);

      if (saleValueNode) {
        productNode.classList.add(defaultProductSettings.classes.withSaleValue);
      }
    }

    EventBus.publish('prices-calculated:insales:ui_accessories', {
      isForCartUpdate,
      productNode,
      chosenAccessoriesNodes,
      priceFromCart,
      priceWithoutAccessores: priceFromCart || price,
      oldPrice
    });

    return;
  }

  const accessoriesPrice = chosenAccessoriesNodes.reduce((acc, el) => {
    const accessoryValuesItemPrice = el.closest(self.options.accessoryValuesItem.attr)
      .querySelector(self.options.accessoryValuesitemPrice.attr)
      .dataset[self.options.accessoryValuesitemPrice.data];

    return acc + parseFloat(accessoryValuesItemPrice);
  }, 0);

  const priceWithAccessories = price + accessoriesPrice;

  self.updatePrice(productNode, optionsProduct.priceFromCart.attr, priceFromCart || priceWithAccessories);
  self.updatePrice(productNode, optionsProduct.price.attr, priceWithAccessories);

  if (oldPriceNode && oldPrice && (oldPrice <= priceWithAccessories || oldPrice <= priceFromCart)) {
    productNode.classList.remove(
      defaultProductSettings.classes.withOldPrice,
      defaultProductSettings.classes.withSaleValue
    );

    productNode.classList.add(defaultProductSettings.classes.withoutOldPrice);
  } else if (oldPriceNode && oldPrice) {
    productNode.classList.remove(defaultProductSettings.classes.withoutOldPrice);
    productNode.classList.add(defaultProductSettings.classes.withOldPrice);

    if (saleValueNode) {
      productNode.classList.add(defaultProductSettings.classes.withSaleValue);
    }
  }

  EventBus.publish('prices-calculated:insales:ui_accessories', {
    isForCartUpdate,
    productNode,
    chosenAccessoriesNodes,
    accessoriesPrice,
    priceWithAccessories,
    priceFromCart,
    priceWithoutAccessores: priceFromCart || price,
    oldPrice
  });
};

/**
 * @private
 * @description
 * Метод для поиска в корзине варианта товара с определённым набором опций
 * @param {Object} productData - данные о товаре
 * @param {Object} accessoriesData - данные о выбранных в карточке товара аксессуарах
 * @return {Number} - цена товара из корзины
 */
AccessoriesDOM.prototype.getPriceFromCart = function(productData, accessoriesData) {
  // Проверка совпадения набора опций или отсутствия опций
  if (
      (productData.accessory_value_ids && haveSameKeys(productData.accessory_value_ids, accessoriesData))
      || (productData.accessory_lines && !productData.accessory_lines.length && !Object.keys(accessoriesData).length)
    ) {
    return productData.sale_price;
  }

  // Поиск подходящего элемента в корзине
  const cartItem = Array.from(window.InsalesCommonCart.order.getItemByID(productData.id, true))
    .find(item => (
      !item.accessory_value_ids && !Object.keys(accessoriesData).length
    ) || (
      item.accessory_value_ids && haveSameKeys(item.accessory_value_ids, accessoriesData)
    ));

  return cartItem ? cartItem.sale_price : null;
};

/**
 * @private
 * @description
 * Обновление цены
 * @param {Object} productNode - нода товара
 * @param {String} selector - селектор цены
 * @param {Number} value - новое значение цены
 * @return {void}
 */
AccessoriesDOM.prototype.updatePrice = (productNode, selector, value) => {
  const element = productNode.querySelector(selector);

  if (element && !_isNaN(value)) {
    element.innerHTML = Shop.money.format(value);
  }
};

/**
 * @private
 *
 * @description
 * Получение текста из словаря базовых сообщений
 *
 * @param {String} messageName - название сообщения
 * @return {String} текст сообщения
 */
AccessoriesDOM.prototype._getMessageByLocale = function(messageName) {
  const self = this;

  if (typeof self.locale !== 'undefined') {
    let result_message = self.defaultSettings[messageName][self.locale];

    if (typeof result_message === 'undefined') {
      result_message = self.defaultSettings[messageName].ru;
    }

    return result_message;
  }

  return self.defaultSettings[messageName].ru;
};

/**
 * Вывод текстовых ошибок для опций в карточке товара
 * @param {Object} cartData - данные из корзины
 * @param {Object} productJSON - JSON товара
 * @param {HTMLElement} productNode - нода товара
 *
 * @return {void}
 */
AccessoriesDOM.prototype.highlightErrors = function(cartData, productJSON, productNode) {
  if (
    // Если метод вызван не для добавления товара в корзину
    (cartData.action.method !== 'add_items' && cartData.action.method !== 'add_checkout') || !cartData.errors.length
    // Или нет кнопки которая вызвала это добавление
    || (!cartData.action.button && !cartData.action.button.length)
  ) {
    return;
  }

  const self = this;

  const currentProductNode = cartData.action.button[0].closest('[data-product-id]');

  if (!currentProductNode || productNode !== currentProductNode) { return; }

  const accessoriesNode = currentProductNode.querySelector(self.options.accessories.attr);

  if (!accessoriesNode) { return; }

  cartData.errors.forEach(arrayErrors => {
    if (typeof arrayErrors !== 'object' || !arrayErrors.length) { return; }

    arrayErrors.forEach(err => {
      if (productJSON.id !== err.product_id) { return; }

      const productAccessoriesData = getProductAccessoriesData(currentProductNode);

      if (!haveSameKeys(productAccessoriesData, err.variant_accessory_values.accessory_value_ids)) { return; }

      const accessoryNode = accessoriesNode
        .querySelector(`[${self.options.accessoriesItemId.value}="${ err.accessory_id }"]`);

      if (!accessoryNode) { return; }

      accessoryNode.classList.add(defaultSettings.classes.hasError);

      const accessoryErrorNode = accessoryNode.querySelector(self.options.accessoryError.attr);

      if (!accessoryErrorNode) { return; }

      const optionsData = (
        accessoryErrorNode.dataset[self.options.accessoryError.data]
        ? JSON.parse(accessoryErrorNode.dataset[self.options.accessoryError.data]) : {}
      );

      const messageText = self.getErrorMessage(err, optionsData);

      accessoryErrorNode.innerHTML = messageText;
    });
  });

  // Если нашли ошибки, то шлём событие для виджета уведомлений и скроллим к первой ноде с ошибкой
  const firstNodeWithError = accessoriesNode.querySelector(`.${defaultSettings.classes.hasError}`);

  if (!firstNodeWithError) { return; }

  const textErrorNode = firstNodeWithError.querySelector(self.options.accessoryError.attr);

  if (textErrorNode) {
    const optionsData = (
      textErrorNode.dataset[self.options.accessoryError.data]
      ? JSON.parse(textErrorNode.dataset[self.options.accessoryError.data]) : {}
    );

    if (optionsData.scrollEnabled && optionsData.scrollEnabled === 'true') {
      const accessoryNodePosition = firstNodeWithError.getBoundingClientRect().top + window.scrollY - 54;
      window.scrollTo({
        top: accessoryNodePosition,
        behavior: 'smooth'
      });
    }
  }

  EventBus.publish('accessories-errors:insales:ui_accessories', {
    currentProductNode,
    accessoriesNode,
    productJSON
  });
};

/**
 * @description
 * Получение текста ошибки для опции
 * @param {Object} errorObject - объект с ошибкой
 * @param {Object} optionsData - значение data-атрибута для локализации
 *
 * @return {String} messageText
 */
AccessoriesDOM.prototype.getErrorMessage = function(errorObject, optionsData) {
  const self = this;

  let messageText = '';

  if (errorObject.min_count > errorObject.count_selected_values) {
    messageText =
      optionsData.lessThenMinCount || `${self._getMessageByLocale('lessThenMinCount')}:&nbsp;${errorObject.min_count}`;
  }

  if (errorObject.max_count < errorObject.count_selected_values) {
    messageText =
      optionsData.moreThenMaxCount || `${self._getMessageByLocale('moreThenMaxCount')}:&nbsp;${errorObject.max_count}`;
  }

  return messageText;
};

export default _Singleton(AccessoriesDOM).getInstance();
